<!--
  PlacementMapping.vue -- Phase 4.6 single-placement row inside the
  per-product mapping preview.

  Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
  Pebble: Phase 4.6 BulkApplySetModal (full UI).

  Renders one row of placement-verdict data from the preview_apply
  response. For "remap" or "drop" verdicts, the user can override the
  target zone (or explicitly drop) via the in-row select. The "fit"
  verdict has no override UI -- it lands exactly where the set placement
  was designed for.

  The component is purely presentational: it emits a ``remap`` event
  with the chosen slug (or null for drop). The parent owns the override
  state in the bulkApplyPreview Pinia store.

  The "__drop__" sentinel in the select option value encodes the drop
  choice -- HTML <select> values are strings, so a distinct sentinel
  keeps the emit clean (we map back to null at the boundary).

  Decoration Model Refactor P5 PR-F (Boulders #163,
  https://trello.com/c/S5kN5poU): when the verdict's class_member_zones
  has 2+ entries, render an ADDITIONAL ambiguity-resolver dropdown so
  the user can pick a sibling zone in the same class. The deterministic
  default (the verdict's resolved_zone_id == the lowest-id member) is
  pre-selected. Selecting a non-default zone emits ``remap`` with that
  slug; the apply payload picks it up via the existing remap_overrides
  field (no new backend contract). This is distinct from the
  remap/drop dropdown above -- ambiguity surfaces ONLY for class-
  resolved verdicts; the remap/drop dropdown surfaces for verdicts
  where the math itself wants to remap or drop.
-->
<template>
    <div class="placement-mapping" data-testid="placement-mapping">
        <span class="placement-mapping__source">
            {{ verdict.set_placement_zone_slug || '(unbound)' }}
        </span>
        <span class="placement-mapping__arrow">&rarr;</span>
        <span class="placement-mapping__target">
            {{ effectiveTargetLabel }}
        </span>
        <span
            class="placement-mapping__verdict"
            :class="verdictClass"
            data-testid="placement-mapping-verdict"
        >
            {{ verdictLabel }}
        </span>
        <select
            v-if="showRemap"
            class="placement-mapping__remap"
            data-testid="placement-mapping-remap-select"
            :value="selectValue"
            @change="onChange"
        >
            <option
                v-for="zone in availableZones"
                :key="zone.slug"
                :value="zone.slug"
            >
                Remap to: {{ zone.label }}
            </option>
            <option value="__drop__">Drop</option>
        </select>
        <select
            v-if="showAmbiguityResolver"
            class="placement-mapping__ambiguity"
            data-testid="placement-mapping-ambiguity-select"
            :value="ambiguitySelectValue"
            :aria-label="`Zone choice within class (${classMemberZones.length} options)`"
            @change="onAmbiguityChange"
        >
            <option
                v-for="member in classMemberZones"
                :key="member.slug"
                :value="member.slug"
            >
                Zone: {{ memberLabel(member) }}
            </option>
        </select>
    </div>
</template>

<script>
export default {
    name: 'PlacementMapping',
    props: {
        verdict: {
            type: Object,
            required: true,
        },
        remapOverride: {
            // undefined = no override; null = explicit drop; string = slug.
            type: [String, null],
            default: undefined,
        },
        availableZones: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['remap'],
    computed: {
        showRemap() {
            return (
                this.verdict.verdict === 'remap' ||
                this.verdict.verdict === 'drop'
            )
        },
        classMemberZones() {
            // Defensive: tolerate missing field (older preview responses
            // pre-P5 PR-F) -- treat as empty list, suppress dropdown.
            const list = this.verdict.class_member_zones
            return Array.isArray(list) ? list : []
        },
        showAmbiguityResolver() {
            // P5 PR-F: ambiguity dropdown surfaces ONLY when the
            // verdict resolved via a class with 2+ active zones AND
            // the verdict actually has a target zone (not a drop --
            // the remap/drop dropdown above handles drops).
            return (
                this.classMemberZones.length >= 2
                && this.verdict.target_zone_slug != null
            )
        },
        ambiguitySelectValue() {
            // Pre-select: the user override slug if one exists in the
            // ambiguity space; otherwise the verdict's default
            // (target_zone_slug). The default zone is the lowest-id
            // class member (the backend's deterministic pick).
            if (
                typeof this.remapOverride === 'string'
                && this.classMemberZones.some(
                    (m) => m.slug === this.remapOverride,
                )
            ) {
                return this.remapOverride
            }
            return this.verdict.target_zone_slug
        },
        verdictLabel() {
            switch (this.verdict.verdict) {
                case 'fit':
                    return 'ok'
                case 'remap':
                    return 'remap'
                case 'drop':
                    return 'drop'
                default:
                    return this.verdict.verdict || ''
            }
        },
        verdictClass() {
            return `placement-mapping__verdict--${this.verdict.verdict}`
        },
        effectiveTarget() {
            // User override wins; null override = explicit drop;
            // undefined = use the server's default.
            if (this.remapOverride === null) return null
            if (typeof this.remapOverride === 'string') {
                return this.remapOverride
            }
            return this.verdict.target_zone_slug
        },
        effectiveTargetLabel() {
            if (this.effectiveTarget == null) return 'not supported'
            const z = this.availableZones.find(
                (x) => x.slug === this.effectiveTarget,
            )
            return z ? z.label : this.effectiveTarget
        },
        selectValue() {
            if (this.effectiveTarget == null) return '__drop__'
            return this.effectiveTarget
        },
    },
    methods: {
        onChange(event) {
            const raw = event?.target?.value ?? ''
            if (raw === '__drop__') {
                this.$emit('remap', null)
                return
            }
            this.$emit('remap', raw)
        },
        memberLabel(member) {
            // Use the catalog's human label when we can match it; fall
            // back to the raw slug (kebab -> Title Case-ish) so the
            // user always sees something readable even if availableZones
            // didn't carry the member.
            const z = this.availableZones.find(
                (x) => x.slug === member.slug,
            )
            if (z && z.label) return z.label
            return member.slug
        },
        onAmbiguityChange(event) {
            const raw = event?.target?.value ?? ''
            if (!raw) return
            // P5 PR-F: emit the chosen slug. The bulkApplyPreview store
            // already stores slug-keyed remap_overrides per
            // (cp_id, set_placement_id); the apply endpoint's
            // _resolve_target_zone accepts slug overrides today, so
            // no new wire field is required.
            this.$emit('remap', raw)
        },
    },
}
</script>

<style scoped>
.placement-mapping {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    font-size: 13px;
    flex-wrap: wrap;
}
.placement-mapping__source,
.placement-mapping__target {
    color: #1e293b;
}
.placement-mapping__arrow {
    color: #94a3b8;
}
.placement-mapping__verdict {
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
}
.placement-mapping__verdict--fit {
    background: #dcfce7;
    color: #166534;
}
.placement-mapping__verdict--remap {
    background: #fef3c7;
    color: #92400e;
}
.placement-mapping__verdict--drop {
    background: #fee2e2;
    color: #991b1b;
}
.placement-mapping__remap {
    margin-left: auto;
    padding: 3px 6px;
    font-size: 12px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
}
.placement-mapping__ambiguity {
    margin-left: auto;
    padding: 3px 6px;
    font-size: 12px;
    border: 1px solid #93c5fd;
    background: #eff6ff;
    border-radius: 4px;
}
</style>
